import React, { useState, useEffect, useRef } from 'react';
import { CalculatorForm } from './CalculatorForm';
import { ResultCard } from '../ResultCard';
import { HistoryTable } from '../History/HistoryTable';
import { ActionBar } from '../ActionBar';
import { saveToHistory, getHistory, clearHistory, deleteHistoryEntry } from '../../utils/storage/history';
import { downloadCSV, parseCSV } from '../../utils/csv';
import { calculatorHistory as calculatorHistoryMapper } from '../../utils/csv/mappers';
import { nanoid } from 'nanoid';
import type { CalculatorInputs, VialMetrics, HistoryEntry } from '../../types/calculator';
import { calculateVialMetrics } from '../../utils/calculations/metrics';
import { CalculateDosageForm } from '../CalculateDosage/CalculateDosageForm';

const initialInputs: CalculatorInputs = {
  peptideName: '',
  vialAmount: 0,
  vialUnits: 'mg',
  dosage: 0,
  dosageUnits: 'mcg',
  targetSyringeUnits: 0,
};

export const Calculator: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'form' | 'dosage'>('dosage');
  const [inputs, setInputs] = useState<CalculatorInputs>(initialInputs);
  const [errors, setErrors] = useState<Partial<Record<keyof CalculatorInputs, string>>>({});
  const [results, setResults] = useState<VialMetrics | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const resultCardRef = useRef<HTMLDivElement | null>(null); // Ref for the ResultCard section

  useEffect(() => {
    setHistory(getHistory());
  }, []);

  const validateInputs = (): boolean => {
    const newErrors: Partial<Record<keyof CalculatorInputs, string>> = {};

    if (!inputs.vialAmount || inputs.vialAmount <= 0) {
      newErrors.vialAmount = 'Please enter a valid vial amount';
    }
    if (!inputs.dosage || inputs.dosage <= 0) {
      newErrors.dosage = 'Please enter a valid dosage';
    }
    if (!inputs.targetSyringeUnits || inputs.targetSyringeUnits <= 0) {
      newErrors.targetSyringeUnits = 'Please enter valid syringe units';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCalculate = () => {
    if (validateInputs()) {
      const metrics = calculateVialMetrics(inputs);
      setResults(metrics);

      const historyEntry: HistoryEntry = {
        id: nanoid(),
        timestamp: Date.now(),
        inputs: { ...inputs },
        metrics,
      };

      saveToHistory(historyEntry);
      setHistory(getHistory());

      // Scroll to the result card after calculation
      if (resultCardRef.current) {
        resultCardRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  const handleReset = () => {
    setInputs(initialInputs);
    setErrors({});
    setResults(null);
  };

  const handleInputChange = (updates: Partial<CalculatorInputs>) => {
    setInputs((prev) => ({ ...prev, ...updates }));
  };

  const handleDeleteHistoryEntry = (id: string) => {
    deleteHistoryEntry(id);
    setHistory(getHistory());
  };

  const handleClearHistory = () => {
    clearHistory();
    setHistory([]);
  };

  const handleExportHistory = () => {
    const columns = calculatorHistoryMapper.calculatorHistoryHeaders.map((header) => ({
      header,
      accessor: (item: HistoryEntry) => calculatorHistoryMapper.toCSV(item)[header],
    }));

    downloadCSV(history, columns, 'calculator-history');
  };

  const handleImportHistory = async (csvData: string) => {
    try {
      const parsed = await parseCSV(csvData);
      const imported = parsed.map((row) => {
        const entry = calculatorHistoryMapper.fromCSV(row);
        return {
          ...entry,
          id: nanoid(),
        } as HistoryEntry;
      });

      imported.forEach(saveToHistory);
      setHistory(getHistory());
    } catch (error) {
      console.error('Failed to import calculator history:', error);
      alert('Failed to import CSV file. Please check the file format.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-4">
        <button
          className={`px-4 py-2 ${activeTab === 'dosage' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('dosage')}
        >
          Graphical
        </button>
        <button
          className={`px-4 py-2 mr-2 ${activeTab === 'form' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          onClick={() => setActiveTab('form')}
        >
          Advanced
        </button>
      </div>

      {activeTab === 'form' ? (
        <CalculatorForm
          inputs={inputs}
          errors={errors}
          onInputChange={handleInputChange}
          onCalculate={handleCalculate}
          onReset={handleReset}
        />
      ) : (
        <CalculateDosageForm
          inputs={inputs}
          errors={errors}
          onInputChange={handleInputChange}
          onCalculate={handleCalculate}
          onReset={handleReset}
        />
      )}

      {results && (
        <div ref={resultCardRef}>
          <ResultCard metrics={results} />
        </div>
      )}

      <ActionBar
        onClear={handleClearHistory}
        onExport={handleExportHistory}
        onImport={handleImportHistory}
        onSample={handleImportHistory}
      />

      <HistoryTable
        history={history}
        onDelete={handleDeleteHistoryEntry}
        onClearAll={handleClearHistory}
      />
    </div>
  );
};
